/**
 * Customer support: history over REST, live messages over SSE.
 *
 * The same shape as the ride chat, for the same reasons, against a different
 * resource. A support conversation is not ride-scoped: people need help with
 * things that are not a ride, including the things that stopped them signing
 * up, so the conversation belongs to the account rather than to a journey.
 *
 * A customer has at most one unresolved conversation at a time, enforced by a
 * partial unique index on the server. `openSupportConversation` is therefore a
 * resume, not a create: pressing "Speak to customer support" twice returns the
 * same thread with the same history, instead of scattering a person's problem
 * across a new thread each time they come back.
 *
 * The live stream is wired into the same cache entry the history query fills,
 * via onCacheEntryAdded. A component subscribes to one query and receives
 * both; there is no second source of messages to reconcile.
 */

import { api } from "../api";
import { env } from "../../lib/env";
import connectSSE from "../../lib/sse";
import type {
    OpenSupportConversationRequest,
    SendSupportMessageRequest,
    SupportConversation,
    SupportConversationSummary,
    SupportMessage,
    UUID,
} from "../types";

interface RootStateShape {
    auth: {
        accessToken: string | null;
        user: { id: string; firstName?: string } | null;
    };
}

export interface SupportConversationArgs {
    id: UUID;
    /** RFC3339 with nanoseconds. Older than this, for paging back. */
    before?: string;
    /** RFC3339 with nanoseconds. Newer than this. */
    after?: string;
    limit?: number;
}

/**
 * Marks a message the client has drawn but the server has not confirmed.
 *
 * Prefixed rather than reusing the client token verbatim, because the id space
 * of a real message is UUIDs and a provisional row must not be mistaken for
 * one: the server's copy arrives with a different id, and a client that could
 * not tell them apart would either drop the real message or leave the
 * provisional one on screen forever.
 */
const PENDING_PREFIX = "pending:";

let pendingSeq = 0;

function pendingId(clientToken?: string): string {
    pendingSeq += 1;
    return `${PENDING_PREFIX}${clientToken ?? pendingSeq}`;
}

/** Whether this message is still in flight, so the UI can say so. */
export function isPendingSupportMessage(message: SupportMessage): boolean {
    return message.id.startsWith(PENDING_PREFIX);
}

/**
 * Inserts a message in creation order, ignoring one already present.
 *
 * The de-duplication is not defensive tidying, it is load-bearing. A sent
 * message arrives twice by design: once as the body of the POST, and once down
 * the stream, because the stream is what every other participant receives and
 * the sender is not a special case on the server. Without this the sender sees
 * their own message twice and nobody else does.
 */
function insertMessage(messages: SupportMessage[], incoming: SupportMessage): void {
    if (messages.some((m) => m.id === incoming.id)) return;

    const at = incoming.createdAt;
    let index = messages.length;
    while (index > 0 && messages[index - 1].createdAt > at) index -= 1;
    messages.splice(index, 0, incoming);
}

export const supportApi = api.injectEndpoints({
    endpoints: (build) => ({
        /**
         * Get or resume. Idempotent, and never creates a second open
         * conversation for one caller.
         *
         * The response carries the whole conversation, messages included, so it
         * is written straight into the query's cache entry rather than thrown
         * away and fetched again a moment later.
         */
        openSupportConversation: build.mutation<
            SupportConversation,
            OpenSupportConversationRequest
        >({
            query: (body) => ({
                url: "support/conversations",
                method: "POST",
                body,
            }),
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    // `void`, not awaited: seeding the cache is the last thing
                    // this does, and nothing after it depends on the write
                    // having landed.
                    void dispatch(
                        supportApi.util.upsertQueryData(
                            "supportConversation",
                            { id: data.id },
                            data
                        )
                    );
                } catch {
                    // Reported to the caller through the hook's error state.
                    // Seeding the cache is an optimisation; the query fetches
                    // for itself when this does not happen.
                }
            },
        }),

        /**
         * One conversation, kept live for as long as something is subscribed.
         *
         * The SSE connection opens when the first subscriber arrives and closes
         * when the last one leaves, which is what `cacheEntryRemoved` resolving
         * means. Opening it in a component's effect instead is how you end up
         * with one connection per mounted component.
         */
        supportConversation: build.query<
            SupportConversation,
            SupportConversationArgs
        >({
            query: ({ id, ...params }) => ({
                url: `support/conversations/${id}`,
                params,
            }),
            providesTags: (_r, _e, { id }) => [
                { type: "SupportConversation", id },
            ],

            async onCacheEntryAdded({ id }, lifecycle) {
                // Called through `lifecycle` rather than destructured: these
                // are methods on the API object, and pulling them off it loses
                // the receiver they are bound to.
                let disconnect: (() => void) | null = null;
                try {
                    // Wait for the first successful fetch: streaming into a
                    // cache entry that has no data yet throws.
                    await lifecycle.cacheDataLoaded;

                    disconnect = connectSSE({
                        url: `${env.apiBaseUrl}support/conversations/${id}/stream`,
                        // Read at connect time, so a reconnection after a token
                        // refresh uses the new token rather than the expired one
                        // this closure was created with.
                        token: () =>
                            (
                                lifecycle.getState() as unknown as RootStateShape
                            ).auth.accessToken,

                        onEvent: (event) => {
                            if (event.event === "message") {
                                let message: SupportMessage;
                                try {
                                    message = JSON.parse(
                                        event.data
                                    ) as SupportMessage;
                                } catch {
                                    // A frame we cannot parse is a frame we
                                    // drop. The reconnection replay brings it
                                    // back if it mattered.
                                    return;
                                }
                                lifecycle.updateCachedData((draft) => {
                                    insertMessage(draft.messages, message);
                                });
                                return;
                            }

                            if (event.event === "conversation") {
                                let summary: SupportConversationSummary;
                                try {
                                    summary = JSON.parse(
                                        event.data
                                    ) as SupportConversationSummary;
                                } catch {
                                    return;
                                }
                                lifecycle.updateCachedData((draft) => {
                                    // Status and assignment only. `unread` is
                                    // deliberately left alone: the customer is
                                    // looking at this conversation, the server
                                    // computed that count before it saw the
                                    // read, and copying it in here would put
                                    // the badge back moments after it cleared.
                                    draft.status = summary.status;
                                    draft.agentName = summary.agentName;
                                    draft.lastMessageAt = summary.lastMessageAt;
                                });
                                return;
                            }

                            // "ready", "lagged" and "expired" are connection
                            // lifecycle, not content. `lagged` and `expired`
                            // both end in a reconnect that replays from
                            // Last-Event-ID, so nothing is lost by ignoring
                            // them here.
                        },
                    });

                    await lifecycle.cacheEntryRemoved;
                } finally {
                    disconnect?.();
                }
            },
        }),

        sendSupportMessage: build.mutation<
            { message: SupportMessage },
            { id: UUID } & SendSupportMessageRequest
        >({
            query: ({ id, ...body }) => ({
                url: `support/conversations/${id}/messages`,
                method: "POST",
                body,
            }),
            // No invalidation: the stream delivers the sent message back, and
            // insertMessage discards the duplicate. Refetching the whole
            // conversation on every send would undo the point of the stream.
            async onQueryStarted({ id, body, clientToken }, lifecycle) {
                // `getState` is read off `lifecycle` rather than destructured,
                // for the same reason the stream's methods are: it is a method
                // on the API object and pulling it off loses its receiver.
                const { dispatch, queryFulfilled } = lifecycle;
                const me = (lifecycle.getState() as unknown as RootStateShape)
                    .auth.user;
                const provisional: SupportMessage = {
                    id: pendingId(clientToken),
                    conversationId: id,
                    senderId: me?.id ?? null,
                    authorRole: "customer",
                    senderName: me?.firstName ?? "You",
                    body,
                    createdAt: new Date().toISOString(),
                };

                // Optimistic, because a support message is often sent on a bad
                // connection and a composer that empties itself with nothing to
                // show for it reads as a message that was lost.
                const patch = dispatch(
                    supportApi.util.updateQueryData(
                        "supportConversation",
                        { id },
                        (draft) => {
                            // `createdAt` here is the browser's clock, and the
                            // order of the list is the server's. A machine a
                            // few minutes behind would otherwise file the
                            // message the person just sent up in the middle of
                            // the history, so it never sorts before the last
                            // one already on screen.
                            const last = draft.messages[draft.messages.length - 1];
                            if (last && last.createdAt > provisional.createdAt) {
                                provisional.createdAt = last.createdAt;
                            }
                            insertMessage(draft.messages, provisional);
                        }
                    )
                );

                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        supportApi.util.updateQueryData(
                            "supportConversation",
                            { id },
                            (draft) => {
                                // Swap rather than undo-then-insert: the stream
                                // may already have delivered the server's copy,
                                // in which case insertMessage is a no-op and
                                // this is the line that removes the duplicate.
                                const at = draft.messages.findIndex(
                                    (m) => m.id === provisional.id
                                );
                                if (at !== -1) draft.messages.splice(at, 1);
                                insertMessage(draft.messages, data.message);
                            }
                        )
                    );
                } catch {
                    // Take the provisional message back off the screen. The
                    // component puts the text back in the composer, so the
                    // words are not lost, they are just not claimed as sent.
                    patch.undo();
                }
            },
        }),

        markSupportConversationRead: build.mutation<null, UUID>({
            query: (id) => ({
                url: `support/conversations/${id}/read`,
                method: "PUT",
            }),
            async onQueryStarted(id, { dispatch, queryFulfilled }) {
                // Optimistic: the unread badge should clear the moment the
                // conversation is opened, not a round trip later.
                const patch = dispatch(
                    supportApi.util.updateQueryData(
                        "supportConversation",
                        { id },
                        (draft) => {
                            draft.unread = 0;
                        }
                    )
                );
                try {
                    await queryFulfilled;
                } catch {
                    patch.undo();
                }
            },
        }),
    }),
});

export const {
    useOpenSupportConversationMutation,
    useSupportConversationQuery,
    useSendSupportMessageMutation,
    useMarkSupportConversationReadMutation,
} = supportApi;
